import type { accountsTypes } from 'app-components/flux/accounts';
import { accountsUtils } from 'app-components/flux/accounts';
import type { institutionLoginsTypes } from 'app-components/flux/institution-logins';
import type { FundingAccount } from './types';
import { EligigibilityGroupEnum, verificationStatusEnum } from './types';

//
// In this file and through the app for A2A, we use two definitions fo an account. It's STATUS and its ELIGIBILITY
// STATUS: This determines what STATE an account is in. 'Microdeposit State' vs. 'Transfers Enabled' state and etc.
// ELIGIBLE: This determines if the behavior the STATE defines is ENABLED or not!
//

// feature can be "transfers", "microdeposit verifications", or "money transfers"
const explanations = {
  // We are attempting to create a funding account for an account that already has one connected (Set up account for transfers flow)
  accountBeingSetup: (feature: string) => `This account is already being setup for ${feature}`,
  accountClosed: (feature: string) => `Closed accounts are not eligible for ${feature}`,
  accountError: (feature: string) => `Accounts in error are not eligible for ${feature}`,
  accountIsManual: (feature: string) => `Manual accounts are not eligible for ${feature}`,
  // We are attempting to open an account in the wrong flow (I.E. attempting to enable an acct for microdeposits that is already verified, etc.)
  wrongFundingAccType: (feature: string) => `This account is not in the correct state to be eligible for ${feature}`,
  // We are attempting to verify a funding account that does not exist
  noFundingAccount: (feature: string) => `This account has not been setup for ${feature}`,
  accountWrongSubTypeInvest:
    'Only personal, U.S.-based checking, savings, and investment accounts that have been connected in Simplifi are eligible.',
  accountWrongTypeInvest:
    'Only personal, U.S.-based checking, savings, and investment accounts that have been connected in Simplifi are eligible.',
  accountWrongSubType:
    'Only personal, U.S.-based checking and savings accounts that have been connected in Simplifi are eligible.',
  accountWrongType:
    'Only personal, U.S.-based checking and savings accounts that have been connected in Simplifi are eligible.',
  accountDisconnected: 'Account must be reconnected before it can be used for transfers.',
  isPending: 'Verification deposits are pending. Check back in 1-2 business days.',
  // This string is not used anywhere since we have a new error screen for it
  verificationFailure: `Verification deposits could not be delivered to this account`,
  // This string is not used anywhere since we have a new error screen for it
  tooManyAttemptsFailure: `The maximum number of verification attempts has been exceeded`,
  noInstitutionLogin: 'The institution for this account does not exist',
  noAccount: 'This account does not exist',
  // Old error leftover from intuit auto verification days, left just in case re-implementation is done.
  notSetupForManual: 'We are working on verifying your deposits, please check in later',
};

type ReasonsObject = {
  noAccount: boolean;
  noFundingAccount?: boolean;
  noInstitutionLogin: boolean;
  fundingAccountExists?: boolean;
  wrongAccountType: boolean;
  verificationFailure?: boolean;
  tooManyAttemptsFailure?: boolean;
  wrongFundingAccType?: boolean;
  isPending?: boolean;
  wrongSubType: boolean;
  isManual: boolean;
  isClosed?: boolean;
  isError?: boolean;
  isDisconnected: boolean;
  notSetupForManualVerification?: boolean;
};

const getReasonString = (reasons: ReasonsObject, label: string, includeInvest: boolean) => {
  if (reasons.notSetupForManualVerification) {
    return explanations.notSetupForManual;
  }
  if (reasons.noAccount) {
    return explanations.noAccount;
  }
  if (reasons.noFundingAccount) {
    return explanations.noFundingAccount(label);
  }
  if (reasons.isManual) {
    return explanations.accountIsManual(label);
  }
  if (reasons.verificationFailure) {
    return explanations.verificationFailure;
  }
  if (reasons.tooManyAttemptsFailure) {
    return explanations.tooManyAttemptsFailure;
  }
  if (reasons.isPending) {
    return explanations.isPending;
  }
  if (reasons.wrongAccountType) {
    return includeInvest ? explanations.accountWrongTypeInvest : explanations.accountWrongType;
  }
  if (reasons.wrongSubType) {
    return includeInvest ? explanations.accountWrongSubTypeInvest : explanations.accountWrongSubType;
  }
  if (reasons.isClosed) {
    return explanations.accountClosed(label);
  }
  if (reasons.noInstitutionLogin) {
    return explanations.noInstitutionLogin;
  }
  if (reasons.isError) {
    return explanations.accountError(label);
  }
  if (reasons.isDisconnected) {
    return explanations.accountDisconnected;
  }
  if (reasons.wrongFundingAccType) {
    return explanations.wrongFundingAccType(label);
  }
  if (reasons.fundingAccountExists) {
    return explanations.accountBeingSetup(label);
  }
  return '';
};

export const eligibleAccountTransferMainTypes = ['BANK', 'INVESTMENT'];

export const validSourceFundingAccountSubTypes = ['CHECKING', 'SAVINGS', 'MONEY_MARKET', 'CASH_MANAGEMENT'];
export const allPotentialFundingAccountSubTypes = [
  'CHECKING',
  'SAVINGS',
  'MONEY_MARKET',
  'CASH_MANAGEMENT',
  'BROKERAGE',
  'IRA',
  'ROTH_IRA',
  '529',
  'UGMA_UTMA',
  'KEOGH',
  'SEP_IRA',
  'SIMPLE_IRA',
  'UNKNOWN',
  'OTHER_INVESTMENTS',
];

export const doesAccountSubTypeSupportA2a = (account?: accountsTypes.Account) => {
  return allPotentialFundingAccountSubTypes.includes(account?.aggregators?.first()?.subType || '');
};

export const isAccountA2aDepositOnly = (account?: accountsTypes.Account) => {
  return !validSourceFundingAccountSubTypes.includes(account?.aggregators?.first()?.subType || '');
};

// this check is evaluated before fundingAccount creation
export const isAccountEligibleForTransfersUtil = (
  account?: accountsTypes.Account,
  fundingAccount?: FundingAccount,
  institutionLogin?: institutionLoginsTypes.InstitutionLogin,
  hideInvestments?: boolean,
) => {
  const reasons: ReasonsObject = {
    noAccount: !account,
    noInstitutionLogin: !institutionLogin,
    wrongAccountType:
      !account?.type ||
      (hideInvestments ? account.type !== 'BANK' : !eligibleAccountTransferMainTypes.includes(account.type)),
    fundingAccountExists: Boolean(account?.fundingAccountId && fundingAccount),
    wrongSubType: !doesAccountSubTypeSupportA2a(account),
    isManual: accountsUtils.isAccountManual(account),
    isClosed: account?.isClosed,
    isError: accountsUtils.isAccountInError(account)?.isError,
    isDisconnected: !!accountsUtils.isAccountDisconnected(account, institutionLogin),
  };

  //  Must NOT have a fundingAccount, must NOT be manual, must NOT be closed
  const status =
    !reasons.noAccount &&
    !reasons.noInstitutionLogin &&
    !reasons.fundingAccountExists &&
    !reasons.isManual &&
    !reasons.isClosed;

  //  Must be of type BANK, must NOT have a fundingAccount, must contain an eligible subType, must NOT be manual, must NOT be closed, must NOT be in error, must NOT be disconnected
  const isEligible =
    !reasons.noAccount &&
    !reasons.noInstitutionLogin &&
    !reasons.wrongAccountType &&
    !reasons.fundingAccountExists &&
    !reasons.wrongSubType &&
    !reasons.isManual &&
    !reasons.isClosed &&
    !reasons.isError &&
    !reasons.isDisconnected;

  const reasonString = getReasonString(reasons, 'transfers', !hideInvestments);

  return {
    isEligible,
    status,
    reasonString,
  };
};

// this is evaluated before initiating microdeposits
export const isAccountEligibleForMicrodepositsUtil = (
  account?: accountsTypes.Account,
  fundingAccount?: FundingAccount,
  institutionLogin?: institutionLoginsTypes.InstitutionLogin,
  hideInvestments?: boolean,
) => {
  const reasons: ReasonsObject = {
    noAccount: !account,
    noFundingAccount: !fundingAccount,
    noInstitutionLogin: !institutionLogin,
    wrongAccountType:
      !account?.type ||
      (hideInvestments ? account.type !== 'BANK' : !eligibleAccountTransferMainTypes.includes(account.type)),
    verificationFailure: fundingAccount?.verificationStatus === verificationStatusEnum.MICRO_DEPOSIT_FAILED,
    tooManyAttemptsFailure:
      fundingAccount?.verificationStatus === verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_FAILED,
    wrongFundingAccType:
      fundingAccount?.verificationStatus !== verificationStatusEnum.MICRO_DEPOSIT_POSTED &&
      fundingAccount?.verificationStatus !== verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_AMOUNTS_INVALID,
    isPending:
      fundingAccount?.verificationStatus === verificationStatusEnum.MICRO_DEPOSIT_INITIATED ||
      fundingAccount?.verificationStatus === verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_CONFIRMED,
    wrongSubType: !doesAccountSubTypeSupportA2a(account),
    isManual: accountsUtils.isAccountManual(account),
    isClosed: account?.isClosed,
    isDisconnected: !!accountsUtils.isAccountDisconnected(account, institutionLogin),
    isError: accountsUtils.isAccountInError(account)?.isError,
  };

  //  Must be of type BANK, must be verification status PENDING, must contain an eligible subType, must NOT be manual, must NOT be closed, must NOT be disconnected
  // This is for ALLOWING THE USER TO ACCESS the microdeposit flow functionality
  const isEligible =
    !reasons.noAccount &&
    !reasons.noFundingAccount &&
    !reasons.noInstitutionLogin &&
    !reasons.wrongAccountType &&
    !reasons.verificationFailure &&
    !reasons.tooManyAttemptsFailure &&
    !reasons.wrongFundingAccType &&
    !reasons.isPending &&
    !reasons.wrongSubType &&
    !reasons.isManual &&
    !reasons.isError &&
    !reasons.isClosed &&
    !reasons.isDisconnected;

  //  Must be verification status PENDING, must NOT be manual, must NOT be closed (Can still be disconnected)
  // This is for letting the user know it is in the microdeposit state, but may not be functional at the moment.
  const status =
    !reasons.noAccount &&
    !reasons.noFundingAccount &&
    !reasons.noInstitutionLogin &&
    !reasons.wrongFundingAccType &&
    !reasons.verificationFailure &&
    !reasons.tooManyAttemptsFailure &&
    !reasons.isPending &&
    !reasons.isManual &&
    !reasons.isClosed;

  const isFailure = reasons.verificationFailure || reasons.tooManyAttemptsFailure;
  const isPending = reasons.isPending && !isFailure; // isFailure overrides the pending state!

  const reasonString = getReasonString(reasons, 'microdeposit verifications', !hideInvestments);

  return {
    isEligible, // Is the funding account eligible to check for microdeposits?
    status, // Do we show the funding account as currently in the microdeposit state at all?
    reasonString, // Why is the funding account not eligible for microdeposits?
    isFailure, // Permanent lockout due to one of the reasons above.
    isPending, // A special state where we don't DISABLE the functionality in an error, we simply do not show it.
  };
};

export const isAccountEligibleForTransferringMoneyUtil = (
  account?: accountsTypes.Account,
  fundingAccount?: FundingAccount,
  institutionLogin?: institutionLoginsTypes.InstitutionLogin,
  hideInvestments?: boolean,
) => {
  const reasons = {
    noAccount: !account,
    noFundingAccount: !fundingAccount,
    noInstitutionLogin: !institutionLogin,
    wrongAccountType:
      !account?.type ||
      (hideInvestments ? account.type !== 'BANK' : !eligibleAccountTransferMainTypes.includes(account.type)),
    wrongFundingAccType: !(
      fundingAccount?.verificationStatus === verificationStatusEnum.MICRO_DEPOSIT_VERIFICATION_COMPLETED ||
      (fundingAccount?.verificationStatus === verificationStatusEnum.VERIFICATION_NOT_NEEDED &&
        fundingAccount?.eligibility === EligigibilityGroupEnum.eligible)
    ),
    wrongSubType: !doesAccountSubTypeSupportA2a(account),
    isManual: accountsUtils.isAccountManual(account),
    isClosed: account?.isClosed,
    isError: accountsUtils.isAccountInError(account)?.isError,
    isDisconnected: !!accountsUtils.isAccountDisconnected(account, institutionLogin),
  };

  //  Must be of type BANK, must be verification status VERIFIED, must contain an eligible subType, must NOT be manual, must NOT be closed, must NOT be in error, must NOT be disconnected
  const isEligible =
    !reasons.noAccount &&
    !reasons.noFundingAccount &&
    !reasons.noInstitutionLogin &&
    !reasons.wrongAccountType &&
    !reasons.wrongFundingAccType &&
    !reasons.wrongSubType &&
    !reasons.isManual &&
    !reasons.isClosed &&
    !reasons.isError &&
    !reasons.isDisconnected;

  //  Must be verification status VERIFIED, must NOT be manual, must NOT be closed
  const status =
    !reasons.noAccount &&
    !reasons.noFundingAccount &&
    !reasons.noInstitutionLogin &&
    !reasons.wrongFundingAccType &&
    !reasons.isManual &&
    !reasons.isClosed;

  const reasonString = getReasonString(reasons, 'money transfers', !hideInvestments);

  return {
    isEligible,
    status,
    reasonString,
  };
};
